import { useAutoneTranslation } from '@autone/translations';
import { type AdvancedFiltersState, NewFilterChips } from '@autone/ui';
import { FilterChip } from '@autone/ui/src/features/filters/filters-popover/filter-chips/FilterChip';
import { useCallback, useMemo } from 'react';

import { type APP_NAME } from '../../consts';
import {
  useAssortmentAdvancedFilters,
  useAssortmentGeneralFilters,
} from '../../hooks';
import { useAssortmentSessionStorageAtom } from '../../storage/session-storage';

import { type TableNamesWithAnyFilters } from './types';

interface AssortmentFilterChipProps {
  tableName: TableNamesWithAnyFilters;
}

export const AssortmentFilterChips = ({
  tableName,
}: AssortmentFilterChipProps) => {
  const {
    translatedGeneralFilterOptions,
    selectedGeneralFilterGroupsCount,
    selectedGeneralFilters,
    setSelectedGeneralFilters,
    dynamicFiltersQueryResult,
  } = useAssortmentGeneralFilters(tableName);

  const { t } = useAutoneTranslation<typeof APP_NAME>();
  const [extrasMenuAtom, setExtrasMenuAtom] = useAssortmentSessionStorageAtom(
    `${tableName}.extrasMenu` as const,
  );

  const { matchSystemRecommendationToggle, managedByAutoneToggle } = useMemo(
    () => ({
      matchSystemRecommendationToggle:
        'matchSystemRecommendationToggle' in extrasMenuAtom
          ? extrasMenuAtom.matchSystemRecommendationToggle
          : 'off',
      managedByAutoneToggle:
        'managedByAutoneToggle' in extrasMenuAtom
          ? extrasMenuAtom.managedByAutoneToggle
          : 'off',
    }),
    [extrasMenuAtom],
  );

  const {
    isLoading: isGeneralFiltersLoading,
    isUninitialized: isGeneralFiltersUninitialized,
  } = dynamicFiltersQueryResult ?? {};

  const generalFiltersIsLoading =
    isGeneralFiltersLoading || isGeneralFiltersUninitialized;

  const {
    generalFilterOptions: generalFilterOptionsMemo,
    selectedGeneralFilters: selectedGeneralFiltersMemo,
    setSelectedGeneralFilters: setSelectedGeneralFiltersMemo,
  } = useMemo(
    () =>
      !generalFiltersIsLoading
        ? {
            generalFilterOptions: [
              ...translatedGeneralFilterOptions.geographic,
              ...translatedGeneralFilterOptions.product,
              ...translatedGeneralFilterOptions.location_clusters,
              ...translatedGeneralFilterOptions.sku,
            ],
            selectedGeneralFilters,
            setSelectedGeneralFilters,
          }
        : {},
    [
      generalFiltersIsLoading,
      translatedGeneralFilterOptions,
      selectedGeneralFilters,
      setSelectedGeneralFilters,
    ],
  );

  const {
    translatedAdvancedFilterOptions,
    selectedAdvancedFilters,
    selectedAdvancedFiltersCount,
    setSelectedAdvancedFilters,
  } = useAssortmentAdvancedFilters(tableName);

  const advancedFiltersColumnOptionsMemo = useMemo(
    () =>
      Object.entries(translatedAdvancedFilterOptions.fields).map(
        ([id, translatedDisplayName]) => ({ id, translatedDisplayName }),
      ),
    [translatedAdvancedFilterOptions],
  );

  const selectedAdvancedFiltersMemo = useMemo(
    () => ({
      conditionCombineMode:
        selectedAdvancedFilters?.conditionCombineMode ?? 'and',
      conditions:
        selectedAdvancedFilters?.conditions.map(
          ({ field, operator, value }) => ({
            filterId: field,
            filterConditionId: operator,
            value: value,
          }),
        ) ?? [],
    }),
    [selectedAdvancedFilters],
  );

  const setSelectedAdvancedFiltersMemo = useCallback(
    (state: AdvancedFiltersState | null) => {
      setSelectedAdvancedFilters(
        /**
         * TODO
         *
         * The incompatibility of `NewFilterChips` with these new arguably more appropriately-named
         * advanced filter objects requires these adapter callbacks and memos. This breaks the generic
         * type inference of `conditions`. We can refactor `NewFilterChips` to be more type-agnostic.
         */
        // @ts-expect-error
        !state
          ? null
          : {
              conditionCombineMode: state.conditionCombineMode,
              conditions: state.conditions.map(
                ({ filterId, filterConditionId, value }) => ({
                  field: filterId,
                  operator: filterConditionId,
                  value,
                }),
              ),
            },
      );
    },
    [setSelectedAdvancedFilters],
  );

  const shouldRenderFilterChips =
    selectedGeneralFilterGroupsCount + selectedAdvancedFiltersCount > 0 ||
    managedByAutoneToggle !== 'off' ||
    matchSystemRecommendationToggle !== 'off';

  if (!shouldRenderFilterChips) return null;

  return (
    <div className="flex flex-row gap-2 justify-end flex-wrap">
      <NewFilterChips
        generalFilterOptions={generalFilterOptionsMemo}
        selectedGeneralFilters={selectedGeneralFiltersMemo}
        handleSelectGeneralFilters={setSelectedGeneralFiltersMemo}
        advancedFilterColumnOptions={advancedFiltersColumnOptionsMemo}
        /**
         * The typing here is a bit off. There's no reason why `FilterChips` should be tied to the shape
         * of `AdvancedFiltersState`.
         */
        // @ts-expect-error
        selectedAdvancedFilters={selectedAdvancedFiltersMemo}
        setSelectedAdvancedFilters={setSelectedAdvancedFiltersMemo}
        includeWrapper={false}
      />
      {managedByAutoneToggle !== 'off' && (
        <FilterChip
          id={'managed-by-autone-filter-chip'}
          showX
          filterName={t('quick-filters.managed-by.label')}
          filterCondition={''}
          filterPostfix={
            managedByAutoneToggle === 'autone'
              ? t('quick-filters.managed-by.autone')
              : t('quick-filters.managed-by.you')
          }
          onFilterRemove={() =>
            setExtrasMenuAtom((prev) => ({
              ...prev,
              managedByAutoneToggle: 'off',
            }))
          }
        />
      )}
      {matchSystemRecommendationToggle !== 'off' && (
        <FilterChip
          id={'matches-recommendation-filter-chip'}
          showX
          filterName={
            matchSystemRecommendationToggle === 'yes'
              ? t('quick-filters.matches-recommendation.matches-recommendation')
              : t(
                  'quick-filters.matches-recommendation.does-not-match-recommendation',
                )
          }
          filterCondition={''}
          filterPostfix={''}
          onFilterRemove={() =>
            setExtrasMenuAtom((prev) => ({
              ...prev,
              matchSystemRecommendationToggle: 'off',
            }))
          }
        />
      )}
    </div>
  );
};
